import { compressImage } from './imageCompression';

export const uploadToCloudinary = async (file, opts = {}) => {
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
  if (!cloudName || !uploadPreset) throw new Error('Missing Cloudinary config');

  const {
    folder,
    compress = true,
    maxWidth = 1600,
    maxHeight = 1600,
    quality = 0.75,
    outputType = 'image/jpeg'
  } = opts || {};

  let payload = file;
  if (compress && file && typeof window !== 'undefined') {
    try {
      payload = await compressImage(file, { maxWidth, maxHeight, quality, outputType });
    } catch (e) {
      // Fallback to original file if compression fails
      payload = file;
    }
  }

  const form = new FormData();
  // Ensure filename is provided for the file part
  const fallbackExt = (typeof outputType === 'string' && outputType.includes('jpeg')) ? 'jpg' : (outputType.split('/')[1] || 'dat');
  const fallbackName = (file && file.name) ? file.name : `upload.${fallbackExt}`;
  form.append('file', payload, fallbackName);
  form.append('upload_preset', uploadPreset);
  if (folder) form.append('folder', folder);

  // Pick resource type by payload mime
  let resourceType = 'image';
  try {
    const mime = (payload && payload.type) || (file && file.type) || '';
    if (typeof mime === 'string' && mime) {
      if (mime.startsWith('video/')) resourceType = 'video';
      else if (!mime.startsWith('image/')) resourceType = 'auto';
    }
  } catch { }
  const endpoint = `https://api.cloudinary.com/v1_1/${cloudName}/${resourceType}/upload`;
  const res = await fetch(endpoint, { method: 'POST', body: form });
  const raw = await res.text();
  let json = null;
  try {
    json = raw ? JSON.parse(raw) : null;
  } catch { }
  if (!res.ok) {
    const message = json?.error?.message || raw || 'Cloudinary upload failed';
    throw new Error(message);
  }
  if (process.env.NODE_ENV !== 'production') {
    try { console.debug('[Cloudinary] Upload response:', json || raw); } catch { }
  }
  let url = json?.secure_url || json?.url || '';
  if (!url && json) {
    const publicId = json.public_id;
    const version = json.version; // may be undefined
    const format = json.format;
    const resourceType = json.resource_type || 'image';
    if (publicId) {
      const safePublicId = String(publicId).replace(/^\/+/, '');
      const ext = format ? `.${format}` : '';
      const ver = version ? `v${version}/` : '';
      url = `https://res.cloudinary.com/${cloudName}/${resourceType}/upload/${ver}${safePublicId}${ext}`;
    }
  }
  // If still no URL but raw text exists, attempt to extract via regex
  if (!url && raw) {
    try {
      const secureRe = /"secure_url"\s*:\s*"(https?:\/\/[^"]+)/;
      const plainRe = /"url"\s*:\s*"(https?:\/\/[^"]+)/;
      const secureMatch = raw.match(secureRe);
      const plainMatch = raw.match(plainRe);
      const pick = (secureMatch && secureMatch[1]) || (plainMatch && plainMatch[1]) || '';
      // Replace escaped forward slashes (\/ -> /)
      url = pick ? pick.replace(/\\\//g, '/') : '';
    } catch { }
  }
  // If still no URL, reconstruct from public_id/format/version in raw
  if (!url && raw) {
    try {
      const pubMatch = raw.match(/"public_id"\s*:\s*"([^"]+)"/);
      const fmtMatch = raw.match(/"format"\s*:\s*"([^"]+)"/);
      const verMatch = raw.match(/"version"\s*:\s*(\d+)/);
      const rtMatch = raw.match(/"resource_type"\s*:\s*"([^"]+)"/);
      const pid = pubMatch && pubMatch[1];
      if (pid) {
        const fmt = fmtMatch && fmtMatch[1];
        const ver = verMatch && verMatch[1] ? `v${verMatch[1]}/` : '';
        const rt = (rtMatch && rtMatch[1]) || resourceType || 'image';
        const ext = fmt ? `.${fmt}` : '';
        url = `https://res.cloudinary.com/${cloudName}/${rt}/upload/${ver}${pid}${ext}`;
      }
    } catch { }
  }
  // Final domain-based fallback: grab any res.cloudinary.com URL for our cloud
  if (!url && raw) {
    try {
      const escapedCloud = cloudName.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&');
      const pattern = "https://res\\.cloudinary\\.com/" + escapedCloud + "/[^\"']+";
      const domainRe = new RegExp(pattern, 'i');
      const m = raw.match(domainRe);
      if (m && m[0]) {
        url = m[0];
      }
    } catch { }
  }
  if (!url) throw new Error('Cloudinary upload succeeded but no URL returned');
  return url;
};
